import { getFormError } from "utils/getFormError";
import { Card, Collapse, Form, FormInstance, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import React, { ReactNode, useEffect } from "react";
import useNotification from "utils/hooks/useNotification";
import useCostumizationApi from "../../hooks/useCostumizationApi";
import useFooterStore from "views/hooks/useFooterStore";
import { TextEditor } from "@neonumy/ant-design";
import {
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaClipboardList,
  FaShoppingCart,
  FaLayerGroup,
  FaCheckCircle,
} from "react-icons/fa";

const { Panel } = Collapse;

type EmailFormValues = Omit<CompanyEmailTemplateInput, "companyId">;

interface EmailFormPropsTypes {
  form: FormInstance<EmailFormValues>;
}

interface SectionHeaderProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
}

function SectionHeader({ icon, title, subtitle }: SectionHeaderProps) {
  return (
    <div className="email-section-header">
      <span className="email-section-header__icon">{icon}</span>
      <div className="email-section-header__text">
        <span className="email-section-header__title">{title}</span>
        <span className="email-section-header__subtitle">{subtitle}</span>
      </div>
    </div>
  );
}

interface EmailFieldGroupProps {
  label: string;
  titleField: string;
  contentField: string;
  t: (key: string) => string;
}

function EmailFieldGroup({ label, titleField, contentField, t }: EmailFieldGroupProps) {
  return (
    <div className="email-field-group">
      <Typography.Text className="email-field-group__label">{label}</Typography.Text>
      <Form.Item name={titleField} label={t("Title")}>
        <TextEditor height={20} allowImageUpload={false} />
      </Form.Item>
      <Form.Item name={contentField} label={t("Body")}>
        <TextEditor height={200} allowImageUpload={false} />
      </Form.Item>
    </div>
  );
}

export default function EmailForm(props: EmailFormPropsTypes): React.ReactElement {
  const { form } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const tabName = location.pathname.split("/").pop();

  const urlParams: RouteParams = useParams();
  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const setHandleOkSaveButton = useFooterStore(state => state.setHandleOkSaveButton);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);

  const { notificationSuccessfullySaved, notificationError } = useNotification();

  const {
    getCompanyEmailTemplate,
    emailTemplateData: data,
    getCompanyEmailTemplateLoading,
    updateCompanyEmailTemplate,
    updateTemplateLoading,
    refetch,
  } = useCostumizationApi();

  useEffect(() => {
    getCompanyEmailTemplate({
      variables: {
        companyId: urlParams?.companyId,
      },
    });
  }, []);

  useEffect(() => {
    if (handleOkSaveButton && tabName === "email") {
      handleOk();
    }
  }, [handleOkSaveButton]);

  useEffect(() => {
    if (data) {
      const fieldsData: { name: string; value: unknown }[] = [];
      for (const key in data) {
        const value = (data as Record<string, unknown>)[key];

        fieldsData.push({
          name: key,
          value: value,
        });
      }
      form.setFields(fieldsData);
    }
  }, [data]);

  const onFinish = async (values: EmailFormValues) => {
    let errors: ErrorType[] | undefined = [];
    const variables = {
      input: {
        companyId: urlParams.companyId,
        title: values.title,
        content: values.content,
        titleReminder1: values.titleReminder1,
        contentReminder1: values.contentReminder1,
        titleReminder2: values.titleReminder2,
        contentReminder2: values.contentReminder2,
        titleReminder3: values.titleReminder3,
        contentReminder3: values.contentReminder3,
        titleOrderReminder: values.titleOrderReminder,
        contentOrderReminder: values.contentOrderReminder,
        titleReminderGroupedDocuments: values.titleReminderGroupedDocuments,
        contentReminderGroupedDocuments: values.contentReminderGroupedDocuments,
        titleBudget: values.titleBudget,
        titleSaleOrder: values.titleSaleOrder,
        contentBudget: values.contentBudget,
        contentSaleOrder: values.contentSaleOrder,
        titleThankYouForPayment: values.titleThankYouForPayment,
        contentThankYouForPayment: values.contentThankYouForPayment,
      },
    };

    try {
      const response = await updateCompanyEmailTemplate({ variables });

      errors = response?.data?.updateCompanyEmailTemplate?.errors;

      if (errors && errors.length > 0) {
        const fieldsData = getFormError(errors, values);
        notificationError(errors);
        form.setFields(fieldsData);
      } else {
        refetch();
        notificationSuccessfullySaved(t("Email settings"));
      }
    } finally {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields: { name: (string | number)[]; errors: string[] }[];
  }) => {
    if (errorFields) {
      setHandleOkSaveButton(false);
      setSaveLoading(false);
    }
  };

  const handleOk = () => {
    form.submit();
    setSaveLoading(true);
  };

  return (
    <Card
      loading={updateTemplateLoading || getCompanyEmailTemplateLoading}
      className="antd-newrai-card"
    >
      <Form
        name="emailForm"
        id="customTemplate"
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Collapse accordion defaultActiveKey={["invoice"]} className="email-collapse">
          <Panel
            header={
              <SectionHeader
                icon={<FaFileInvoiceDollar />}
                title={t("Invoice")}
                subtitle={t("Email template for invoices")}
              />
            }
            key="invoice"
          >
            <EmailFieldGroup label={t("Invoice")} titleField="title" contentField="content" t={t} />
          </Panel>

          <Panel
            header={
              <SectionHeader
                icon={<FaExclamationTriangle />}
                title={t("Follow ups")}
                subtitle={t("Dunning reminders for unpaid invoices")}
              />
            }
            key="followUps"
          >
            <EmailFieldGroup
              label={t("Dunning invoice Level 1")}
              titleField="titleReminder1"
              contentField="contentReminder1"
              t={t}
            />
            <EmailFieldGroup
              label={t("Dunning invoice Level 2")}
              titleField="titleReminder2"
              contentField="contentReminder2"
              t={t}
            />
            <EmailFieldGroup
              label={t("Dunning invoice Level 3")}
              titleField="titleReminder3"
              contentField="contentReminder3"
              t={t}
            />
          </Panel>

          <Panel
            header={
              <SectionHeader
                icon={<FaClipboardList />}
                title={t("Budget")}
                subtitle={t("Email template for budget documents")}
              />
            }
            key="budget"
          >
            <EmailFieldGroup
              label={t("Budget")}
              titleField="titleBudget"
              contentField="contentBudget"
              t={t}
            />
          </Panel>

          <Panel
            header={
              <SectionHeader
                icon={<FaShoppingCart />}
                title={t("Sale order")}
                subtitle={t("Email template for sale orders and reminders")}
              />
            }
            key="saleOrder"
          >
            <EmailFieldGroup
              label={t("Sale order")}
              titleField="titleSaleOrder"
              contentField="contentSaleOrder"
              t={t}
            />
            <EmailFieldGroup
              label={t("Budget/Order reminder")}
              titleField="titleOrderReminder"
              contentField="contentOrderReminder"
              t={t}
            />
          </Panel>

          <Panel
            header={
              <SectionHeader
                icon={<FaLayerGroup />}
                title={t("Reminder for group documents")}
                subtitle={t("Email template for grouped document reminders")}
              />
            }
            key="reminderGroupedDocuments"
          >
            <EmailFieldGroup
              label={t("Reminder for group documents")}
              titleField="titleReminderGroupedDocuments"
              contentField="contentReminderGroupedDocuments"
              t={t}
            />
          </Panel>

          <Panel
            header={
              <SectionHeader
                icon={<FaCheckCircle />}
                title={t("Payment confirmation")}
                subtitle={t("Email template for payment acknowledgements")}
              />
            }
            key="paymentConfirmation"
          >
            <EmailFieldGroup
              label={t("Payment confirmation")}
              titleField="titleThankYouForPayment"
              contentField="contentThankYouForPayment"
              t={t}
            />
          </Panel>
        </Collapse>
      </Form>
    </Card>
  );
}
